// DocSharkAPI.js

const API_SEARCH_URL = `${process.env.PUBLIC_URL}/api-docshark/search`;
const API_SEARCH_COUNT_URL = `${process.env.PUBLIC_URL}/api-docshark/search_count`;
const API_HIGHLIGHTS_URL = `${process.env.PUBLIC_URL}/api-docshark/highlights`;
const API_HEALTH_URL = `${process.env.PUBLIC_URL}/api-docshark/health`;
const API_SEARCH_IMAGES_URL = `${process.env.PUBLIC_URL}/api-docshark/search_images`;
const API_IMAGE_HIGHLIGHTS_URL = `${process.env.PUBLIC_URL}/api-docshark/image_highlights`;
const API_GET_IMAGES_BATCH_URL = `${process.env.PUBLIC_URL}/api-docshark/get_images_batch`; // New API endpoint

export const performSearch = async (searchQuery, selectedScope = [], selectedFileTypes = [], includeImageMetadata = false, setLoadingStage) => { // Renamed includeImagesInResults for clarity
  let combinedResults = [];
  let textSearchTime = 0;
  let imageSearchTime = 0;

  try {
    setLoadingStage('Searching...');
    const textResponse = await fetch(API_SEARCH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: searchQuery,
        file_types: selectedFileTypes,
        eu_pnumbers: selectedScope,
        limit: 10000, // Consider if this limit is appropriate
      }),
    });

    if (!textResponse.ok) {
      throw new Error(`HTTP error! status: ${textResponse.status} while fetching text results`);
    }
    const textData = await textResponse.json();
    combinedResults = textData.results || [];
    textSearchTime = textData.search_time || 0;

    if (includeImageMetadata) { // If true, we fetch image metadata (paths, captions, etc.)
      setLoadingStage('Searching📷');
      const imageResponse = await fetch(API_SEARCH_IMAGES_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                query: searchQuery,
                file_types: selectedFileTypes,
                eu_pnumbers: selectedScope,
                limit: 20000, // Consider if this limit is appropriate
                include_images: false,
            }),
        });

      if (!imageResponse.ok) {
            console.error(`HTTP error! status: ${imageResponse.status} while fetching image metadata. Proceeding with text results.`);
      } else {
        const imageData = await imageResponse.json();
        imageSearchTime = imageData.search_time || 0;
        if (imageData.results && imageData.results.length > 0) {
          // Ensure image results are uniquely identifiable if there are overlaps with text results based on keys.
          // For now, direct concat; ensure `groupedResults` in DocSharkResults.js handles this.
          combinedResults = combinedResults.concat(imageData.results.map(imgRes => ({...imgRes, _isImageHit: true})));
        }
      }
    }
    
    setLoadingStage('Processing results...');
    const totalSearchTime = textSearchTime + imageSearchTime;
    return { results: combinedResults, search_time: totalSearchTime };

  } catch (error) {
    console.error("API call failed in performSearch:", error);
    throw error;
  }
};

export const getSearchCount = async (searchQuery, selectedScope = [], selectedFileTypes = []) => {
  try {
    const response = await fetch(API_SEARCH_COUNT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: searchQuery,
        file_types: selectedFileTypes,
        eu_pnumbers: selectedScope,
        limit: 10000,
      }),
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
};

export const getHighlights = async (filename, query, maxFragments = 5, fragmentSize = 100) => {
  const url = API_HIGHLIGHTS_URL;
  const body = { filename, query, max_fragments: maxFragments, fragment_size: fragmentSize };
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("API call for text highlights failed:", error);
    throw error;
  }
};

export const getImageHighlightsFromAPI = async (filename, query, maxFragments = 5, fragmentSize = 150) => {
  // 'filename' here is image_path)
  const url = API_IMAGE_HIGHLIGHTS_URL;
  const body = {
    filename: filename, 
    query: query,
    max_fragments: maxFragments,
    fragment_size: fragmentSize,
  };
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      if (response.status === 404) {
        return { highlights: [] }; 
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json(); 
  } catch (error) {
    console.error("API call for image highlights failed:", error);
    throw error;
  }
};

// New function to fetch batch image data with optional thumbnail size
export const getImagesBatchFromAPI = async (imagePaths, thumbnailSize = null) => {
  if (!imagePaths || imagePaths.length === 0) {
    return { images: [] }; // Return empty if no paths provided
  }
  try {
    const response = await fetch(API_GET_IMAGES_BATCH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ 
        image_paths: imagePaths, 
        thumbnail_size: thumbnailSize // Pass thumbnailSize to the API
      }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} while fetching images batch`);
    }
    return await response.json(); // Expected: { images: [{ image_path: string, image_data_uri?: string, error?: string }] }
  } catch (error) {
    console.error("API call for batch images failed:", error);
    // Return a structure indicating error for all requested paths
    return { 
      images: imagePaths.map(path => ({ image_path: path, error: error.message || "Failed to fetch" })) 
    };
  }
};

// New function to fetch a single full-resolution image
export const getFullImageFromAPI = async (imagePath) => {
  if (!imagePath) {
    return { image: null };
  }
  try {
    const response = await fetch(API_GET_IMAGES_BATCH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ image_paths: [imagePath] }), // Request a single image without thumbnail_size
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} while fetching full image`);
    }
    const data = await response.json();
    return { image: data.images && data.images.length > 0 ? data.images[0] : null };
  } catch (error) {
    console.error("API call for full image failed:", error);
    return { image: { image_path: imagePath, error: error.message || "Failed to fetch full image" } };
  }
};

export const checkHealth = async () => {
  try {
    const response = await fetch(API_HEALTH_URL, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return response.ok;
  } catch (error) {
    console.error("API call failed:", error);
    return false;
  }
};
